import {
    FiArrowDownCircle,
    FiArrowUpCircle,
    FiChevronDown,
} from 'react-icons/fi';
import { IconButton, SoftButton } from '../Button';
import { MenuItem, MenuItems } from '../Button/MenuButton';
import DefinitionTooltip from 'components/DefinitionTooltip';
import { Listbox } from '@headlessui/react';
import Select from './Select';
import SemanticList from '../SemanticList';
import Stack from '../Stack';
import { cn } from '@/ui/design-system/utils/cn';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { views } from '@/copy';

function MaybeTooltipOption({
    children,
    week,
    year,
    tooltip,
    mixpanelContentKey,
}) {
    if (!(tooltip && mixpanelContentKey)) return children;
    return (
        <DefinitionTooltip
            {...tooltip}
            week={week}
            year={year}
            mixpanelMeta={{ contentKey: mixpanelContentKey }}
        >
            {children}
        </DefinitionTooltip>
    );
}

const SortSelectLabel = styled.span`
    color: ${({ theme }) => theme.gray_3};
    font-weight: ${({ theme }) => theme.fontSemiBold};
    &::after {
        content: ':';
    }
`;

const SortSelectContent = styled(Stack).attrs(
    ({ $direction = 'row', $space = (theme) => `${theme.space_3}px` }) => ({
        $direction,
        $space,
    }),
)`
    display: flex;
    flex-direction: row;
    align-items: center;
    line-height: 0.8em;
`;

const SortSelectButton = forwardRef(({ value, ...props }, ref) => {
    const SortIcon = value.ascending ? FiArrowUpCircle : FiArrowDownCircle;

    return (
        <Listbox.Button {...props} ref={ref}>
            <SortSelectContent>
                <SortSelectLabel>Sort By</SortSelectLabel>
                <span css="font-size: 1.29em;">
                    <SortIcon />
                </span>
                <span>{value.label}</span>
            </SortSelectContent>
        </Listbox.Button>
    );
});

const SelectOptions = styled(
    forwardRef(({ as = SemanticList, $menuAlign = 'left', ...props }, ref) => (
        <MenuItems ref={ref} as={as} $menuAlign={$menuAlign} {...props} />
    )),
)`
    min-width: max-content;
    max-width: 25em;

    & > li > div {
        display: flex;
        flex-direction: column;
    }
`;

const SelectOptionWrapper = MenuItem;

const SelectOption = forwardRef(
    (
        {
            active,
            selected,
            option,
            week,
            year,
            tooltip,
            mixpanelContentKey,
            format: _format,
            ...props
        },
        ref,
    ) => (
        <MaybeTooltipOption
            sortBy={option.sortBy}
            week={week}
            year={year}
            tooltip={tooltip}
            mixpanelContentKey={mixpanelContentKey}
        >
            <div>
                <SelectOptionWrapper
                    active={active}
                    selected={selected}
                    IconLeft={
                        option.ascending ? FiArrowUpCircle : FiArrowDownCircle
                    }
                    className={cn(
                        'w-full capitalize',
                        active || selected ? 'active' : '',
                    )}
                    {...props}
                    ref={ref}
                >
                    {option.label}{' '}
                    {option.ascending ? views.ascending : views.descending}
                </SelectOptionWrapper>
            </div>
        </MaybeTooltipOption>
    ),
);

const SortSelect = forwardRef(
    (
        {
            mixpanelContext = 'SortSelect',
            handleMixpanelEvent = ({ sortBy, ascending }) => ({
                contentKey: sortBy,
                ascending,
            }),
            SortSelectButton: SortSelectButtonComponent = SortSelectButton,
            buttonProps: buttonPropsProp,
            SelectButton = SortSelectButton,
            SelectOptions: SelectOptionsComponent = SelectOptions,
            SelectOption: SelectOptionComponent = SelectOption,
            ...props
        },
        ref,
    ) => {
        const buttonProps = {
            as: IconButton,
            IconRight: FiChevronDown,
            size: 'sm',
            ...buttonPropsProp,
        };
        return (
            <Select
                mixpanelContext={mixpanelContext}
                handleMixpanelEvent={handleMixpanelEvent}
                SortSelectButton={SortSelectButtonComponent}
                buttonProps={buttonProps}
                SelectButton={SelectButton}
                SelectOptions={SelectOptionsComponent}
                SelectOption={SelectOptionComponent}
                {...props}
                ref={ref}
            />
        );
    },
);

const GraphNavSortSelectButton = forwardRef(({ value, ...props }, ref) => {
    return (
        <MaybeTooltipOption
            sortBy={value.sortBy}
            week={props?.week}
            year={props?.year}
            tooltip={value?.tooltip}
            mixpanelContentKey={value?.mixpanelContentKey}
        >
            <Listbox.Button {...props} ref={ref}>
                {value.value}
            </Listbox.Button>
        </MaybeTooltipOption>
    );
});

export function GraphNavSortSelect({
    mixpanelContext = 'SortSelect',
    handleMixpanelEvent = ({ sortBy, ascending }) => ({
        contentKey: sortBy,
        ascending,
    }),
    SortSelectButton: SortSelectButtonComponent = SortSelectButton,
    buttonProps = {
        as: SoftButton,
        IconRight: FiChevronDown,
        className: 'capitalize text-foreground',
    },
    SelectButton = GraphNavSortSelectButton,
    ...props
}) {
    return (
        <SortSelect
            mixpanelContext={mixpanelContext}
            handleMixpanelEvent={handleMixpanelEvent}
            SortSelectButton={SortSelectButtonComponent}
            buttonProps={buttonProps}
            SelectButton={SelectButton}
            {...props}
        />
    );
}

export function MetricSelect({
    mixpanelContext = 'SortSelect',
    handleMixpanelEvent = ({ sortBy, ascending }) => ({
        contentKey: sortBy,
        ascending,
    }),
    SortSelectButton: SortSelectButtonComponent = SortSelectButton,
    buttonProps = {
        as: MetricSelectButton,
        IconRight: FiChevronDown,
    },
    SelectButton = GraphNavSortSelectButton,
    SelectOption = MetricSelectOption,
    ...props
}) {
    return (
        <SortSelect
            mixpanelContext={mixpanelContext}
            handleMixpanelEvent={handleMixpanelEvent}
            SortSelectButton={SortSelectButtonComponent}
            buttonProps={buttonProps}
            SelectButton={SelectButton}
            SelectOption={SelectOption}
            {...props}
        />
    );
}

const MetricSelectButton = styled(SoftButton)`
    font-size: 0.75rem;
    font-weight: ${({ theme }) => theme.fontBold};
    color: inherit;
    padding: 0;
    border: 0;
    height: auto;
    &:hover {
        background: transparent;
    }
`;

const MetricSelectOption = forwardRef(
    (
        {
            active,
            selected,
            label,
            week,
            year,
            tooltip,
            mixpanelContentKey,
            format: _format,
            ...props
        },
        ref,
    ) => (
        <MaybeTooltipOption
            week={week}
            year={year}
            tooltip={tooltip}
            mixpanelContentKey={mixpanelContentKey}
        >
            <div>
                <SelectOptionWrapper
                    active={active}
                    selected={selected}
                    className={active || selected ? 'active' : ''}
                    ref={ref}
                    {...props}
                >
                    {label}
                </SelectOptionWrapper>
            </div>
        </MaybeTooltipOption>
    ),
);

export default SortSelect;
